// Owns the agent chat state machine for the panel: the rendered item list,
// streaming flag, current conversation id, sending, and confirm/post-back.
// Supports "allow once" (confirm) and "allow always" (persist per action key,
// then auto-confirm future Tier-A proposals of that action).

import { useCallback, useRef, useState } from 'react';
import { agentAPI } from '../services/agentApi';
import type {
  AgentProposal,
  AgentConversationDetail,
  PageContext,
} from '../types/agent';

export type ChatItem =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'assistant'; text: string }
  | { id: string; kind: 'tool_status'; tool: string; label: string }
  | {
      id: string;
      kind: 'proposal';
      proposal: AgentProposal;
      status: 'pending' | 'executing' | 'done' | 'failed';
    }
  | { id: string; kind: 'outcome'; ok: boolean; text: string }
  | { id: string; kind: 'error'; message: string };

let _seq = 0;
function nextId(): string {
  _seq += 1;
  return `item-${_seq}`;
}

// --- allow-always persistence (Tier A only), keyed by catalog action_key ---
const ALLOW_KEY = 'admin_agent_allow_always';
function loadAllow(): Set<string> {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(ALLOW_KEY) || '[]'));
  } catch {
    return new Set<string>();
  }
}
function isAllowedAlways(actionKey?: string): boolean {
  return !!actionKey && loadAllow().has(actionKey);
}
function addAllowAlways(actionKey: string): void {
  const s = loadAllow();
  s.add(actionKey);
  localStorage.setItem(ALLOW_KEY, JSON.stringify([...s]));
}

export interface UseAgentChat {
  items: ChatItem[];
  streaming: boolean;
  conversationId: number | null;
  sendMessage: (message: string, pageContext?: PageContext) => Promise<void>;
  confirmProposal: (itemId: string) => Promise<void>;
  allowAlways: (itemId: string) => Promise<void>;
  loadConversation: (id: number) => Promise<void>;
  reset: () => void;
}

export function useAgentChat(): UseAgentChat {
  const [items, setItems] = useState<ChatItem[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const conversationIdRef = useRef<number | null>(null);

  // Keep a synchronous mirror of items so confirm/auto-confirm never read stale.
  const itemsRef = useRef<ChatItem[]>([]);
  const updateItems = useCallback(
    (fn: (prev: ChatItem[]) => ChatItem[]) => {
      setItems((prev) => {
        const next = fn(prev);
        itemsRef.current = next;
        return next;
      });
    },
    [],
  );

  // Break the runTurn <-> executeProposalItem cycle via refs.
  const runTurnRef = useRef<(m: string, p?: PageContext) => Promise<void>>();
  const executeRef = useRef<(itemId: string) => Promise<void>>();

  const setConv = (id: number) => {
    conversationIdRef.current = id;
    setConversationId(id);
  };

  const runTurn = useCallback(
    async (message: string, pageContext?: PageContext) => {
      setStreaming(true);
      const assistantId = nextId();
      let assistantStarted = false;
      const autoConfirmIds: string[] = [];

      await agentAPI.streamChat(
        {
          message,
          conversation_id: conversationIdRef.current ?? undefined,
          page_context: pageContext,
        },
        (event) => {
          if (event.type === 'text') {
            updateItems((prev) => {
              if (!assistantStarted) {
                assistantStarted = true;
                return [...prev, { id: assistantId, kind: 'assistant', text: event.delta }];
              }
              return prev.map((it) =>
                it.id === assistantId && it.kind === 'assistant'
                  ? { ...it, text: it.text + event.delta }
                  : it,
              );
            });
          } else if (event.type === 'tool_status') {
            updateItems((prev) => [
              ...prev,
              { id: nextId(), kind: 'tool_status', tool: event.tool, label: event.label },
            ]);
          } else if (event.type === 'proposal') {
            const id = nextId();
            updateItems((prev) => [
              ...prev,
              { id, kind: 'proposal', proposal: event.proposal, status: 'pending' },
            ]);
            if (event.proposal.tier === 'A' && isAllowedAlways(event.proposal.action_key)) {
              autoConfirmIds.push(id);
            }
          } else if (event.type === 'error') {
            updateItems((prev) => [
              ...prev,
              { id: nextId(), kind: 'error', message: event.message },
            ]);
          } else if (event.type === 'done') {
            setConv(event.conversation_id);
          }
        },
      );

      setStreaming(false);

      // Auto-confirm any Tier-A proposals the admin chose to "allow always".
      for (const id of autoConfirmIds) {
        await executeRef.current?.(id);
      }
    },
    [updateItems],
  );
  runTurnRef.current = runTurn;

  const executeProposalItem = useCallback(
    async (itemId: string) => {
      const target = itemsRef.current.find((i) => i.id === itemId);
      if (!target || target.kind !== 'proposal' || target.status !== 'pending') return;
      const proposal = target.proposal;

      updateItems((prev) =>
        prev.map((it) =>
          it.id === itemId && it.kind === 'proposal' ? { ...it, status: 'executing' } : it,
        ),
      );

      // Full text (incl. raw result) is fed to the model so it can summarize;
      // the UI shows only a compact outcome chip, not the raw JSON dump.
      let modelText: string;
      let ok: boolean;
      const label = proposal.action_label || `${proposal.method} ${proposal.endpoint}`;
      try {
        const result = await agentAPI.executeProposal(proposal);
        ok = true;
        updateItems((prev) =>
          prev.map((it) =>
            it.id === itemId && it.kind === 'proposal' ? { ...it, status: 'done' } : it,
          ),
        );
        modelText =
          `Дію виконано: ${proposal.method} ${proposal.endpoint}. ` +
          `Результат: ${JSON.stringify(result)}`;
      } catch (err: any) {
        ok = false;
        updateItems((prev) =>
          prev.map((it) =>
            it.id === itemId && it.kind === 'proposal' ? { ...it, status: 'failed' } : it,
          ),
        );
        modelText =
          `Дію не вдалося виконати: ${proposal.method} ${proposal.endpoint}. ` +
          `Помилка: ${err?.message ?? 'невідома помилка'}`;
      }

      updateItems((prev) => [
        ...prev,
        { id: nextId(), kind: 'outcome', ok, text: ok ? `${label}: виконано` : `${label}: помилка` },
      ]);
      await runTurnRef.current?.(modelText);
    },
    [updateItems],
  );
  executeRef.current = executeProposalItem;

  const confirmProposal = useCallback(
    async (itemId: string) => {
      await executeProposalItem(itemId);
    },
    [executeProposalItem],
  );

  const allowAlways = useCallback(
    async (itemId: string) => {
      const target = itemsRef.current.find((i) => i.id === itemId);
      if (target && target.kind === 'proposal' && target.proposal.action_key) {
        addAllowAlways(target.proposal.action_key);
      }
      await executeProposalItem(itemId);
    },
    [executeProposalItem],
  );

  const sendMessage = useCallback(
    async (message: string, pageContext?: PageContext) => {
      updateItems((prev) => [...prev, { id: nextId(), kind: 'user', text: message }]);
      await runTurn(message, pageContext);
    },
    [runTurn, updateItems],
  );

  const loadConversation = useCallback(
    async (id: number) => {
      const detail: AgentConversationDetail = await agentAPI.getConversation(id);
      setConv(id);
      const loaded: ChatItem[] = detail.messages.map((m) => {
        if (m.role === 'user') {
          return { id: nextId(), kind: 'user', text: String(m.content.text ?? '') };
        }
        if (m.role === 'assistant') {
          return { id: nextId(), kind: 'assistant', text: String(m.content.text ?? '') };
        }
        if (m.role === 'proposal') {
          return {
            id: nextId(),
            kind: 'proposal',
            proposal: m.content as AgentProposal,
            status: 'done',
          };
        }
        return {
          id: nextId(),
          kind: 'tool_status',
          tool: String(m.content.tool ?? ''),
          label: String(m.content.label ?? ''),
        };
      });
      itemsRef.current = loaded;
      setItems(loaded);
    },
    [],
  );

  const reset = useCallback(() => {
    conversationIdRef.current = null;
    setConversationId(null);
    itemsRef.current = [];
    setItems([]);
    setStreaming(false);
  }, []);

  return {
    items,
    streaming,
    conversationId,
    sendMessage,
    confirmProposal,
    allowAlways,
    loadConversation,
    reset,
  };
}
